import { Anchor, Button, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { PiArrowCounterClockwise } from 'react-icons/pi';
import { Link } from 'wouter';
import { reissueAction } from '@/components/InvoiceDetailsModal/changeActions';
import { refusal } from '@/components/InvoicesPage/CancelInvoiceModal';
import type { TInstallmentReissue } from '@/models/invoices';
import { useHasAnyPermission } from '@/models/permissions';
import { useIssueInstallment } from '@/models/split-plan';

export function ReissueInstallment({
  reissue,
}: {
  reissue: TInstallmentReissue | null | undefined;
}) {
  const mayWrite = useHasAnyPermission(['invoices:write', 'invoices:*', '*:*']);
  const mayIssue = useHasAnyPermission([
    'offers:issue-installment-all',
    'offers:*',
    '*:*',
  ]);
  const issue = useIssueInstallment(reissue?.offerId ?? 0);
  const action = reissueAction(reissue, { mayWrite, mayIssue });
  if (!reissue || !action) return null;

  if (action === 'replaced') {
    return (
      <Text fz="xs" c="dimmed">
        Installment {reissue.seq} was issued again as{' '}
        <Anchor
          component={Link}
          to={`~/invoices/${reissue.currentInvoice!.id}`}
          fz="xs"
        >
          {reissue.currentInvoice!.invoiceNumber}
        </Anchor>
        .
      </Text>
    );
  }

  if (action === 'locked' && reissue.viaNewProforma) {
    return (
      <Text fz="xs" c="dimmed">
        Installment {reissue.seq} is issued again by the offer's owner with
        Issue new proforma on{' '}
        <Anchor
          component={Link}
          to={`~/offers/${reissue.offerId}/finance`}
          fz="xs"
        >
          the offer
        </Anchor>
        .
      </Text>
    );
  }

  if (action === 'locked') {
    return (
      <Text fz="xs" c="dimmed">
        Installment {reissue.seq} can be issued again once allowed:{' '}
        {reissue.reason}
      </Text>
    );
  }

  const confirm = () =>
    modals.openConfirmModal({
      title: `Issue installment ${reissue.seq} again?`,
      children: (
        <Text fz="sm">
          A new proforma with a new number is issued for installment{' '}
          {reissue.seq} on the agreed schedule and emailed to the paying party.
          This cancelled proforma stays as it is.
        </Text>
      ),
      labels: { confirm: 'Issue again', cancel: 'Back' },
      onConfirm: () =>
        issue.mutate(reissue.planItemId, {
          onError: (err) =>
            notifications.show({
              color: 'red',
              message: refusal(err) ?? String(err),
            }),
          onSuccess: () =>
            notifications.show({
              color: 'teal',
              message: `Installment ${reissue.seq} issued again.`,
            }),
        }),
    });

  return (
    <Button
      size="xs"
      variant="light"
      leftSection={<PiArrowCounterClockwise size={12} />}
      loading={issue.isPending}
      onClick={confirm}
    >
      Issue installment {reissue.seq} again
    </Button>
  );
}
